import React, { useMemo } from 'react'

// COMPONENTS
import QuoteProcurementPartInfo from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';

// CONSTANTS
import logo from '@/main/assets/images/login/xnuup_main_logo_white.png';
import backgroundXnuup from '@/main/assets/images/login/xnuup_background.png';

//UTILS/SERVICES
import { getTranslateByKey, isEmpty, isNullUndefined } from '@/main/services/utils';
import { OrderPrintResponse } from '@/main/models/response';
import { formatCurrency } from '@/main/services/utilsNumber';
import { createResponsiveColGroup, renderText } from '@/main/services/utilsHtml';

// LOCAL COMPONENTS & STYLES
import { BaseLayoutPDF } from '../baseLayoutPDF';
import * as S from './styles'

interface IDataTable {
  titleBox: string;
  columnsByRow?: number;
  data: ({
    title: string;
    value: string;
  } | null)[];
}
interface IDataTableSuppliers {
  nameSupplier: string;
  registrationNumber: string;
  phoneNumber: string;
  email: string;
  parts: {
    quality: string;
    description: string | JSX.Element;
    dateDelivery: string;
    status: string;
    price?: string;
    paymentVoucherId: string;
  }[];
  totalPartsPrice?: string;
}
interface ITableLayoutProps {
  dataTable: IDataTable;
  columnsByRow?: number;
}

const TableLayout = ({
  dataTable,
  columnsByRow = 3
}: ITableLayoutProps) => {

  const formattedTableBodyValues = dataTable.data.reduce((acc, curr, index) => {
    const rowIndex = Math.floor(index / columnsByRow);
    if (!acc[rowIndex]) {
      acc[rowIndex] = []
    }
    acc[rowIndex].push(curr)
    return acc
  }, [] as ({ title: string, value: string } | null)[][])
  const hasValueTitle = dataTable.titleBox !== ''

  return (
    <table>
      <colgroup>
        {createResponsiveColGroup(columnsByRow)}
      </colgroup>
      <thead>
        <tr>
          <th colSpan={columnsByRow}>
            {hasValueTitle && <S.TitleBox $title={dataTable.titleBox} />}
          </th>
        </tr>
      </thead>
      <tbody>
        {formattedTableBodyValues.map((row, index) => (
          <tr key={index}>
            {row.map((data, index) => (
              <td key={index}>
                {isNullUndefined(data) ? '' : <S.TitleRow $title={data.title}>{data.value}</S.TitleRow>}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
interface ITableLayoutSupplierProps {
  dataTable: IDataTableSuppliers;
  showPrices?: boolean;
}

const TableLayoutSupplier = ({
  dataTable,
  showPrices
}: ITableLayoutSupplierProps) => {

  return (
    <div style={{ margin: '0 .4rem', padding: '0 0 .4rem', border: '1px solid #cccccc', borderRadius: '4px' }}>
      <table>
        <thead>
          <tr>
            <th colSpan={1}>
              <S.TitleBox $title={`${dataTable.nameSupplier} - ${dataTable.registrationNumber}`} />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <S.TitleRow $title={getTranslateByKey('information.contactName')}>{dataTable.nameSupplier}</S.TitleRow>
              <S.TitleRow $title={getTranslateByKey("information.phoneNr")}>{dataTable.phoneNumber}</S.TitleRow>
              <S.TitleRow $title={getTranslateByKey('information.emailAbrevv')}>{dataTable.email}</S.TitleRow>
            </td>
          </tr>
          <tr>
            <td>
              <div style={{ margin: '.4rem 0 0' }}>
                <S.TableTestsSupplier>
                  <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '50%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: showPrices ? '10%' : '15%' }} />
                    {showPrices && <col style={{ width: '5%' }} />}
                  </colgroup>
                  <thead>
                    <tr style={{}}>
                      <th>{getTranslateByKey('information.quality')}</th>
                      <th>{getTranslateByKey('information.description')}</th>
                      <th>{getTranslateByKey('information.DeliveryDate')}</th>
                      <th>{getTranslateByKey('information.status')}</th>
                      {showPrices && <th>{getTranslateByKey('information.price')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {dataTable.parts.map((part, index) => (
                      <tr key={index}>
                        <td>{part.quality}</td>
                        <td>{part.description}
                          {!isEmpty(part.paymentVoucherId) && <p><b>{getTranslateByKey('information.Voucher')}: {part.paymentVoucherId}</b></p>}
                        </td>
                        <td>{renderText(part.dateDelivery)}</td>
                        <td>{part.status}</td>
                        {showPrices && <td>{part.price}</td>}
                      </tr>
                    ))}
                    {showPrices && (
                      <tr>
                        <td colSpan={4}>{getTranslateByKey('information.total')}</td>
                        <td>{dataTable.totalPartsPrice}</td>
                      </tr>
                    )}
                  </tbody>
                </S.TableTestsSupplier>
              </div>
            </td>
          </tr>
        </tbody>
      </table >
    </div >
  )
}


export interface LayoutIOrderPrintProps {
  dataResponseAsync?: OrderPrintResponse;
  showPrices: boolean;
}
const INITIAL_STATE: OrderPrintResponse = {
  orderCode: 0,
  dateImpression: "",
  suppliers: [],
  insurer: {
    insurerCompanyName: "",
    registrationNumber: ""
  },
  bodyshop: {
    bodyshopCompanyName: "",
    registrationNumber: "",
    countryName: "",
    stateName: "",
    cityName: "",
    postalCode: "",
    streetName: "",
    municipalityName: "",
    phone: "",
    email: "",
    contactName: ""
  },
  claim: {
    claimNumber: "",
    orderDate: new Date(),
    adminData: ""
  },
  vehicle: {
    vin: "",
    vehicleRegistration: "",
    vehicleName: ""
  },
  totalParts: {
    totalPrice: 0,
    totalParts: 0
  }
};
const LayoutOrderPrint = React.forwardRef((props: LayoutIOrderPrintProps, ref: React.Ref<HTMLTableElement>) => {
  const { dataResponseAsync, showPrices } = props;
  const formattedDataResponseAsync = isEmpty(dataResponseAsync) ? INITIAL_STATE : dataResponseAsync;

  function appendObjectCondition<T>(condition: boolean, object: () => T): T[] {
    return condition ? [object()] : []
  }
  const DataInfoRows: IDataTable[] = useMemo(() => {
    const haveInsurer = !isNullUndefined(formattedDataResponseAsync.insurer)
    const haveClaim = !isNullUndefined(formattedDataResponseAsync.claim) && !isNullUndefined(formattedDataResponseAsync.claim.claimNumber)
    const haveAdminData = !isNullUndefined(formattedDataResponseAsync.claim.adminData)

    return ([
      {
        titleBox: '',
        columnsByRow: 2,
        data: [
          { title: getTranslateByKey('information.orderDate'), value: String(formattedDataResponseAsync.claim.orderDate) },
          ...appendObjectCondition(haveClaim, () => ({ title: getTranslateByKey('information.claimNumberSiniestro'), value: formattedDataResponseAsync.claim.claimNumber })),
          null,
          ...appendObjectCondition(haveAdminData, () => ({ title: getTranslateByKey('information.claimNumberSiniestro'), value: formattedDataResponseAsync.claim.adminData }))
        ]
      },
      ...appendObjectCondition(haveInsurer, () => ({
        titleBox: getTranslateByKey('information.insurer'),
        columnsByRow: 2,
        data: [
          { title: getTranslateByKey("information.company"), value: renderText(formattedDataResponseAsync.insurer?.insurerCompanyName) },
          { title: getTranslateByKey("information.rfc"), value: renderText(formattedDataResponseAsync.insurer?.registrationNumber) }
        ]
      })),
      {
        titleBox: getTranslateByKey('information.vehicleData'),
        columnsByRow: 2,
        data: [
          { title: getTranslateByKey("claimReport.columnLicensePlate"), value: renderText(formattedDataResponseAsync.vehicle.vehicleRegistration) },
          { title: getTranslateByKey("information.vehicle"), value: renderText(formattedDataResponseAsync.vehicle.vehicleName) },
          { title: getTranslateByKey("information.vin"), value: renderText(formattedDataResponseAsync.vehicle.vin) }
        ]
      },
      {
        titleBox: getTranslateByKey('information.deliveryLocation'),
        columnsByRow: 2,
        data: [
          { title: getTranslateByKey("companyType.bodyshop"), value: renderText(formattedDataResponseAsync.bodyshop.bodyshopCompanyName) },
          { title: getTranslateByKey("information.rfc"), value: renderText(formattedDataResponseAsync.bodyshop.registrationNumber) },
          { title: getTranslateByKey("information.colony"), value: renderText(formattedDataResponseAsync.bodyshop.municipalityName) },
          { title: getTranslateByKey('information.zipCodeAbbreviated'), value: renderText(formattedDataResponseAsync.bodyshop.postalCode) },
          { title: getTranslateByKey('information.street'), value: renderText(formattedDataResponseAsync.bodyshop.streetName) },
          { title: getTranslateByKey("information.state"), value: renderText(formattedDataResponseAsync.bodyshop.stateName) },
          { title: getTranslateByKey("information.country"), value: renderText(formattedDataResponseAsync.bodyshop.countryName) },
          { title: getTranslateByKey("information.city"), value: renderText(formattedDataResponseAsync.bodyshop.cityName) },
          { title: getTranslateByKey("information.phoneNr"), value: renderText(formattedDataResponseAsync.bodyshop.phone) },
          { title: getTranslateByKey("information.contact"), value: renderText(formattedDataResponseAsync.bodyshop.contactName) },
          { title: getTranslateByKey("information.emailAbrevv"), value: renderText(formattedDataResponseAsync.bodyshop.email) },
        ]
      }
    ])
  }, [JSON.stringify(formattedDataResponseAsync)])



  const dataInfoSuppliers: IDataTableSuppliers[] = useMemo(() => {
    const result: IDataTableSuppliers[] = formattedDataResponseAsync.suppliers.map(supplier => {
      return {
        nameSupplier: renderText(supplier.supplierCompanyName),
        registrationNumber: supplier.registrationNumber,
        phoneNumber: renderText(supplier.phone),
        email: renderText(supplier.email),
        ...(showPrices && { totalPartsPrice: renderText(formatCurrency(supplier.totalPrice)) }),
        parts: supplier.parts.map(part => {
          return {
            dateDelivery: renderText(String(part.deliveredDate)),
            description:
              <QuoteProcurementPartInfo
                data={part.quoteProcurementPartInfoResponse}
                renderType='column'
                showLineSupplierInfo={{ info: 'all' }}
              />,
            ...(showPrices && { price: renderText(formatCurrency(part.price)) }),
            quality: renderText(part.quoteProcurementPartInfoResponse.partType),
            status: renderText(part.statusName),
            paymentVoucherId: part.paymentVoucherId
          }
        })
      }
    });

    return result;
  }, [JSON.stringify(formattedDataResponseAsync)])
  const dataInfoSummary: IDataTable[] = useMemo(() => ([
    {
      titleBox: getTranslateByKey('information.summary'),
      columnsByRow: 2,
      data: [
        { title: getTranslateByKey('information.partsAmount'), value: String(formattedDataResponseAsync.totalParts.totalParts) },
        ...appendObjectCondition(showPrices, () => ({ title: getTranslateByKey('information.totalPrice'), value: formatCurrency(formattedDataResponseAsync.totalParts.totalPrice) })),
      ]
    }

  ]), [JSON.stringify(formattedDataResponseAsync)]);


  return (
    <BaseLayoutPDF.Container ref={ref} >
      <S.BackgroundWatermark src={backgroundXnuup} />

      <BaseLayoutPDF.Header>
        <S.HeaderDocument>
          <S.ImageLogo src={logo} alt="Xnuup" />
          <span>{getTranslateByKey('information.orderPedido')} {`#${formattedDataResponseAsync.orderCode}`} </span>
        </S.HeaderDocument>
      </BaseLayoutPDF.Header>

      <BaseLayoutPDF.Body>
        <S.BodyDocument>
          {DataInfoRows.map((data, index) => (
            <TableLayout key={index} dataTable={data} columnsByRow={data.columnsByRow} />
          ))}

          <S.TitleBox $title={getTranslateByKey('information.suppliers')} $gap='.4rem'>
            {dataInfoSuppliers.map((supplier, index) => (
              <TableLayoutSupplier key={index} dataTable={supplier} showPrices={showPrices} />
            ))}
          </S.TitleBox>
          {dataInfoSummary.map((data, index) => (
            <TableLayout key={index} dataTable={data} columnsByRow={data.columnsByRow} />
          ))}
        </S.BodyDocument>
      </BaseLayoutPDF.Body>

      <BaseLayoutPDF.Footer>
        <span>{getTranslateByKey('information.impression')}: {formattedDataResponseAsync.dateImpression}</span>
      </BaseLayoutPDF.Footer>
    </BaseLayoutPDF.Container >
  )
});

export default LayoutOrderPrint